/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import type { Ref } from 'preact/hooks';
import { ReferenceLine } from '../utils/UNSAFE_meterTypes';

type Dimensions = {
  width: number;
  height: number;
};

/**
 * Returns the metric value of the clicked point.
 * @param event The PointerEvent
 * @param dimensionsRef Ref to stored dimensions of the meter bar.
 * @param min The minimum value of the meter bar.
 * @param max The maximum value of the meter bar.
 * @param step The step value of the meter bar.
 * @param orientation The orientation of the meter bar.
 * @param isRtl Whether the reading mode is 'rtl'
 * @returns The metric value of the clicked point.
 */
export function getValue(
  event: PointerEvent,
  dimensionsRef: Ref<Dimensions>,
  min: number,
  max: number,
  step: number,
  orientation?: 'horizontal' | 'vertical',
  isRtl?: boolean
): number | undefined {
  const dims = dimensionsRef.current;
  if (!dims || dims.width === 0) {
    return undefined;
  }

  let val;

  if (orientation === 'horizontal') {
    const width = event.offsetX;
    val = min + ((max - min) * (isRtl ? dims.width - width : width)) / dims.width;
  } else {
    const height = dims.height - event.offsetY;
    val = min + ((max - min) * height) / dims.height;
  }
  val = Math.round(val / step) * step;
  return Math.max(min, Math.min(max, val));
}

/**
 * Returns the true dimension of the meter bar after first render. All dimensions are 0 for initial render.
 * @param element The meter bar track HTML element
 * @returns The dimension of the meter bar.
 */
export function getDimensions(element: HTMLElement | null): Dimensions {
  if (!element) {
    return {
      width: 0,
      height: 0
    };
  }
  const trackDims = element.getBoundingClientRect();

  return {
    width: trackDims.width,
    height: trackDims.height
  };
}

/**
 * Returns object with 'start' and 'end' keys. Value for each key will be true if at least one ref line with correposnding position is present in meter bar.
 * @param lines The array of reference lines.
 * @returns Returns object with start and end keys.
 */
export function getRefLinesInfo(lines?: ReferenceLine[]): { start: boolean; end: boolean } {
  let refLineInfo = { start: false, end: false };
  if (lines) {
    refLineInfo = lines.reduce((acc, cur) => {
      acc.start = acc.start || cur.position === 'start';
      acc.end = acc.end || cur.position === 'end' || cur.position == null;
      return acc;
    }, refLineInfo);
  }
  return refLineInfo;
}
